'use strict';

/**
 * PictView-Blueprint
 * ------------------
 * The full-page, read-only "blueprint" for a Work Item: every linked artifact
 * (visions, features, documents, media + recordings, whiteboards, sessions,
 * comments, issues) laid out on one drafting sheet. Fetches the enriched
 * /1.0/WorkItem/:id/Blueprint payload and renders a title block + a plate per
 * artifact group, with media and video shown inline.
 *
 * Editors also get the share strip above the sheet (BlueprintShareUI), the same
 * control the compact in-detail blueprint block uses.
 *
 * The HTML/CSS comes from source/BlueprintRender.js, the same module the server
 * uses for the public /shared/:token page, so the two stay identical.
 *
 * Shown via the app's #/work-item/:id/blueprint route (Pict-Application._renderBlueprint).
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libBlueprintRender = require('../BlueprintRender.js');
const libShareUI = require('../BlueprintShareUI.js');
const libLayout = require('../BlueprintLayout.js');

function _esc(pStr)
{
	return String(pStr === null || typeof pStr === 'undefined' ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

class PictViewBlueprint extends libPictView
{
	onBeforeInitialize()
	{
		this._data = null;
		this._id = 0;
		this._loading = false;
		this._share = null;
		this._canShare = false;
		return super.onBeforeInitialize();
	}

	// In-app icons come from the Pict icon provider (the app's set), passed into
	// the shared renderer as its fIcon so the rest of the app's chrome matches.
	_icon(pType)
	{
		let tmpProvider = this.pict && this.pict.providers && this.pict.providers.Icon;
		return tmpProvider ? tmpProvider.get(libBlueprintRender.ICON_FOR[pType] || 'FileText') : '';
	}

	_can(pEntitlement)
	{
		let tmpApp = this.pict.PictApplication;
		return !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can(pEntitlement));
	}

	// Load + paint a blueprint, named either by the entity that owns it or by the blueprint record
	// itself. The caller passes a BlueprintAPI subject and this view no longer knows the difference:
	// it used to string-concatenate an entity key into every URL it built, which is why a blueprint
	// opened as itself could not be read, shared or printed at all.
	show(pSubject)
	{
		let tmpAPI = this._api();
		this._subject = (pSubject && pSubject.ID) ? pSubject : null;
		this._id = this._subject ? this._subject.ID : 0;
		// Kept for the share strip and anything else that still reads a key off the view.
		this._entityKey = this._subject ? this._subject.EntityKey : '';
		this._data = null;
		this._share = null;
		this._versions = [];
		this._loading = true;
		// Any blueprint can be shared: the share row hangs off IDBlueprint, and the public read, the public
		// page and the token-scoped media all resolve through the blueprint's own owner (WI #340).
		this._canShare = this._can('content.edit');
		this.render();
		if (!this._subject || !tmpAPI) { this._loading = false; return; }

		let fBlueprint = tmpAPI.readSubject(this._subject);
		let fShare = this._canShare ? libShareUI.fetchState(this.pict, this._subject) : Promise.resolve(null);
		// The version history of the SHEET, which is keyed on the blueprint row rather than the subject.
		// A subject-shaped id here would list somebody else's history or nothing at all.
		let fVersions = (this._subject && this._subject.EntityKey === 'Blueprint')
			? tmpAPI.versions(this._subject.ID) : Promise.resolve([]);

		Promise.all([fBlueprint, fShare, fVersions]).then((pResults) =>
		{
			this._data = pResults[0] || null;
			// Re-address by record once we know it. Pinning, the by-id share family and the by-id
			// print route all key off IDBlueprint, which only the payload can tell us.
			if (this._data && tmpAPI) { this._subject = tmpAPI.subjectFromPayload(this._data) || this._subject; }
			this._share = pResults[1] || null;
			this._versions = pResults[2] || [];
			// Re-read once the payload has told us the real blueprint id, because the first attempt could
			// only have been right when the page was opened by blueprint rather than through a host entity.
			if (this._subject && this._data && !this._versions.length && tmpAPI)
			{
				tmpAPI.versions(this._subject.ID).then((pRows) =>
				{
					if (!pRows || !pRows.length) { return; }
					this._versions = pRows;
					this.render();
				});
			}
			this._loading = false;
			if (this.pict.PictApplication && typeof this.pict.PictApplication._setPageTitle === 'function') { this.pict.PictApplication._setPageTitle((this._data && this._data.Title) ? (this._data.Title + ' Blueprint') : 'Blueprint'); }
			this.render();
		}).catch(() => { this._data = null; this._loading = false; this.render(); });
	}

	/**
	 * Every version of this sheet, each one openable.
	 *
	 * THE OPEN LINK IS WHY THIS EXISTS. The thing this replaced could issue versions and list them and
	 * never open one: you could see that a snapshot existed and had no way to read it, which makes the
	 * record decorative. A version you cannot retrieve is not a record of anything.
	 *
	 * Renders nothing at all when there are none, rather than an empty box explaining itself. A sheet
	 * gets its first version the moment somebody shares it, so an empty list is a sheet nobody has
	 * handed to anyone -- and that needs no commentary on the page.
	 */
	_versionsHTML()
	{
		let tmpVersions = this._versions || [];
		if (!tmpVersions.length) { return ''; }
		let tmpAPI = this.pict.providers.BlueprintAPI;
		let tmpRows = tmpVersions.map((pVersion) =>
		{
			let tmpWhen = String(pVersion.IssuedAt || '').substring(0, 10);
			let tmpTo = pVersion.IssuedTo ? (' <span class="bp-ver-to">to ' + _esc(pVersion.IssuedTo) + '</span>') : '';
			let tmpSheets = Number(pVersion.SheetCount) || 0;
			let tmpCount = tmpSheets ? ('<span class="bp-ver-n">' + tmpSheets + (tmpSheets === 1 ? ' sheet' : ' sheets') + '</span>') : '';
			let tmpHref = tmpAPI ? tmpAPI.versionURL(pVersion.IDBlueprintRevision, 'Letter', 'Desk') : '#';
			return '<li class="bp-ver">'
				+ '<span class="bp-ver-mark">Rev ' + _esc(pVersion.Revision) + '</span>'
				+ '<span class="bp-ver-when">' + _esc(tmpWhen) + '</span>'
				+ tmpTo + tmpCount
				+ '<a class="bp-ver-open" href="' + _esc(tmpHref) + '" target="_blank" rel="noopener"'
				+ ' title="Open this version exactly as it was frozen">Open</a>'
			+ '</li>';
		}).join('');
		return '<div class="bp-versions"><div class="bp-versions-head">Versions</div>'
			+ '<ol class="bp-versions-list">' + tmpRows + '</ol></div>';
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (typeof document !== 'undefined')
		{
			let tmpShare = document.getElementById('Blueprint-Share');
			if (tmpShare) { tmpShare.innerHTML = (this._canShare && !this._loading && this._data) ? libShareUI.stripHTML(this._share, this._selfRef()) : ''; }
			let tmpVersions = document.getElementById('Blueprint-Versions');
			if (tmpVersions) { tmpVersions.innerHTML = (!this._loading && this._data) ? this._versionsHTML() : ''; }
			let tmpHost = document.getElementById('Blueprint-Sheet');
			if (tmpHost)
			{
				tmpHost.innerHTML = this._sheetHTML();
				// A designed layout fits its text to its cells, same as the studio and the PDF.
				if (this._renderedLayout && typeof libLayout.fitLayout === 'function') { libLayout.fitLayout(tmpHost); }
				// A responsive layout re-fits when the pane resizes (crossing 1:1 swaps the arrangement and
				// re-cells every text region). A fixed layout never reflows, so it attaches no observer.
				if (this._fitObserver) { this._fitObserver.disconnect(); this._fitObserver = null; }
				if (this._renderedLayout && this._responsiveLayout && typeof libLayout.observeFit === 'function')
				{
					this._fitObserver = libLayout.observeFit(tmpHost, tmpHost.querySelector('.bp-layout-sheet'));
				}
			}
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_selfRef() { return "_Pict.views['" + this.Hash + "']"; }

	_api() { return this.pict.providers.BlueprintAPI; }

	// ---- handlers --------------------------------------------------------------
	copyLink()
	{
		let tmpURL = (typeof location !== 'undefined') ? location.href : '';
		let fDone = () =>
		{
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('Blueprint link copied', { type: 'success' }); }
		};
		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(tmpURL).then(fDone, fDone); }
		else { fDone(); }
	}

	// Back to the record this blueprint describes. It follows the ENTITY, not a hardcoded work item: on a
	// module's blueprint, navigating to /work-item/<id> would open whatever work item happens to carry the
	// same number, which is a different record entirely (WI #340).
	back()
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (!tmpRouter) { return; }
		let tmpApp = this.pict.PictApplication;
		// A blueprint opened as itself has no host id to go back to, but its payload names its owner,
		// so it can still return to the record it describes. With no owner at all (a standalone
		// blueprint) the honest destination is the blueprint list, not some work item.
		let tmpKey = (this._subject && this._subject.Kind === 'entity') ? this._subject.EntityKey : ((this._data && this._data.OwnerType) || '');
		let tmpID = (this._subject && this._subject.Kind === 'entity') ? this._subject.ID : Number((this._data && this._data.IDOwner) || 0);
		if (!tmpKey || !tmpID) { return tmpRouter.navigate('/blueprints'); }
		let tmpSegment = (tmpApp && typeof tmpApp._singularRoute === 'function') ? tmpApp._singularRoute(tmpKey) : 'work-item';
		tmpRouter.navigate('/' + tmpSegment + '/' + tmpID);
	}

	// What the back button calls the thing it opens. A blueprint of a module should not offer to open a work
	// item; saying the wrong noun is how a person ends up on the wrong record and believes the page.
	_ownerNoun()
	{
		let tmpAPI = this._api();
		if (this._subject && this._subject.Kind === 'entity' && tmpAPI) { return tmpAPI.ownerNoun(this._subject); }
		let tmpOwner = (this._data && this._data.OwnerType) || '';
		return (tmpAPI && tmpOwner) ? tmpAPI.ownerNoun(tmpAPI.subjectForEntity(tmpOwner, 1)) : 'blueprint';
	}

	/**
	 * Does this blueprint describe some OTHER record, or is it composed and standing on its own?
	 *
	 * A composed blueprint has no owner, and the owner button then labelled itself from the fallback
	 * noun and read "Open blueprint" -- on the blueprint. Clicking it landed on the blueprint LIST, which
	 * is where back() honestly sends a blueprint with nothing behind it. So the one control a person
	 * reaches for first says it will open the thing they are looking at and instead throws the page away.
	 */
	_hasOwner()
	{
		if (this._subject && this._subject.Kind === 'entity') { return true; }
		return !!((this._data && this._data.OwnerType) && Number((this._data && this._data.IDOwner) || 0));
	}

	// BlueprintShareUI calls this after a share change; a full repaint is cheap here
	// (show() owns the fetch, render() only paints).
	_afterShareChange() { this.render(); }

	// Share strip delegates (BlueprintShareUI operates on this view).
	bpShareCreate() { libShareUI.create(this); }
	bpShareCopy() { libShareUI.copy(this); }
	bpShareCopyBadge() { libShareUI.copyBadge(this); }
	bpShareInvite() { libShareUI.invite(this); }
	bpShareRegenerate() { libShareUI.regenerate(this); }
	bpShareRevoke() { libShareUI.revoke(this); }
	bpShareSetExpiry(pValue) { libShareUI.setExpiry(this, pValue); }
	bpSharePrintEnabled(pEnabled) { libShareUI.setPrintEnabled(this, pEnabled); }
	bpShareRepin() { libShareUI.repin(this); }

	/**
	 * Print this blueprint as a sheet set (F-071, WI #384).
	 *
	 * The tab is opened SYNCHRONOUSLY, inside the click, and filled in once the bytes arrive. Opening it
	 * after the fetch resolves is a popup a browser blocks; pointing the tab straight at the route instead
	 * would work but costs a second render to find out a print failed, and renders are not cheap. This way
	 * the server compiles once and a failure closes the tab and says why.
	 *
	 * And when the tab is blocked ANYWAY -- an embedded browser, a strict popup setting -- the bytes are
	 * handed over as a download instead. Measured, not hypothetical: window.open returns null in the
	 * in-app preview browser, and the first version of this quietly did nothing after making the server
	 * compile a whole sheet set. A print that costs a render and produces no file is the worst of both.
	 */
	print()
	{
		let tmpSheet = this._printSheet || 'Letter';
		if (typeof document !== 'undefined')
		{
			let tmpSelect = document.getElementById('Blueprint-PrintSheet');
			if (tmpSelect && tmpSelect.value) { tmpSheet = tmpSelect.value; }
		}
		// REMEMBERED ON THE VIEW, not left in the DOM. Six share delegates call _afterShareChange ->
		// render(), which re-emits the select from scratch; with the choice living only in the element,
		// toggling the public link after choosing a size silently put it back to Letter. Harmless while
		// the only options were Letter and Legal and nobody looked. With five it is the silent-wrong-
		// document failure resolveSheet refuses a bad size to prevent.
		this._printSheet = tmpSheet;
		if (!this._id) { return; }

		let tmpWindow = (typeof window !== 'undefined') ? window.open('', '_blank') : null;
		let fFail = (pMessage) =>
		{
			if (tmpWindow) { tmpWindow.close(); }
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: 'error' }); }
		};

		let tmpFileName = 'sheet-set.pdf';
		// The URL comes from BlueprintAPI so this works for a blueprint addressed as itself, whose print
		// route (/1.0/Blueprint/:id/Print) the server has always had and nothing ever called. The raw
		// fetch stays: this needs the Content-Disposition header and a blob, which the JSON helpers do
		// not carry through.
		fetch(this._api().printURL(this._subject, tmpSheet), { credentials: 'same-origin' })
			.then((pResponse) =>
			{
				if (pResponse.ok)
				{
					// The server already worked out what this set is called; parsing it back beats inventing a
					// second naming scheme here that would drift from the one on the cover.
					let tmpDisposition = pResponse.headers.get('Content-Disposition') || '';
					let tmpMatch = /filename\*=UTF-8''([^;]+)/.exec(tmpDisposition) || /filename="([^"]+)"/.exec(tmpDisposition);
					if (tmpMatch) { try { tmpFileName = decodeURIComponent(tmpMatch[1]); } catch (pE) { tmpFileName = tmpMatch[1]; } }
					return pResponse.blob();
				}
				// The server distinguishes "you asked for something impossible" from "this server cannot
				// print at all", and both carry a message worth showing rather than a generic failure.
				return pResponse.json().then(
					(pBody) => Promise.reject(new Error((pBody && pBody.Error) || 'Could not print this blueprint.')),
					() => Promise.reject(new Error('Could not print this blueprint.')));
			})
			.then((pBlob) =>
			{
				let tmpURL = URL.createObjectURL(pBlob);
				if (tmpWindow) { tmpWindow.location = tmpURL; return; }
				let tmpAnchor = document.createElement('a');
				tmpAnchor.href = tmpURL;
				tmpAnchor.download = tmpFileName;
				document.body.appendChild(tmpAnchor);
				tmpAnchor.click();
				document.body.removeChild(tmpAnchor);
				let tmpModal = this.pict.views['Pict-Section-Modal'];
				if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('Printed ' + tmpFileName, { type: 'success' }); }
			})
			.catch((pError) => fFail((pError && pError.message) || 'Could not print this blueprint.'));
	}

	// ---- rendering -------------------------------------------------------------

	/**
	 * The sheet sizes this server will actually print, ASKED FOR rather than restated here.
	 *
	 * This list used to be two hardcoded options under a comment saying Tabloid and ARCH D had not
	 * landed yet. They landed in WI #382 and #383, the comment did not, and the control went on offering
	 * Letter and Legal over a server that had supported five sizes for months. A list the client cannot
	 * read is a list that drifts, so it reads one now.
	 *
	 * The hardcoded pair survives as the FALLBACK only: a catalog fetch that fails must not take the
	 * print button down with it, and Letter and Legal are the two sizes that have worked since WI #377.
	 */
	_sheetCatalog()
	{
		return Array.isArray(this._sheets) && this._sheets.length
			? this._sheets
			: [ { Sheet: 'Letter', Label: 'Letter (8.5 x 11 in)' }, { Sheet: 'Legal', Label: 'Legal (8.5 x 14 in)' } ];
	}

	/** Load the catalog once, then repaint so the control shows what the server really offers. */
	_loadSheetCatalog()
	{
		if (this._sheetsLoading || (Array.isArray(this._sheets) && this._sheets.length)) { return; }
		this._sheetsLoading = true;
		if (typeof fetch !== 'function') { return; }
		fetch('/1.0/SheetLayouts', { credentials: 'same-origin' })
			.then((pResponse) => (pResponse.ok ? pResponse.json() : null))
			.then((pData) =>
			{
				if (pData && Array.isArray(pData.Sheets) && pData.Sheets.length)
				{
					this._sheets = pData.Sheets;
					this.render();
				}
			})
			// A catalog that will not load leaves the fallback in place. The print button still works at
			// the two sizes everybody has, which is better than a control that vanishes.
			.catch(() => {});
	}

	_printSizesHTML()
	{
		let tmpSelf = this._selfRef();
		this._loadSheetCatalog();
		let tmpChosen = this._printSheet || 'Letter';
		// Ask -- cheaply, no PDF -- whether this size would print any image soft, so the advisory below is
		// ready before somebody commits a whole render only to find a photo blurs on a 24x36 sheet.
		this._ensureResolutionChecked(tmpChosen);
		let tmpOptions = this._sheetCatalog().map((pSheet) =>
			'<option value="' + pSheet.Sheet + '"'
				+ ((pSheet.Sheet === tmpChosen) ? ' selected' : '') + '>'
				+ (pSheet.Label || pSheet.Sheet) + '</option>').join('');
		return '<span class="bp-print-wrap">'
			+ '<select id="Blueprint-PrintSheet" class="bp-print-sheet" title="Sheet size"'
				+ ' onchange="' + tmpSelf + '.bpPrintSheet(this.value)">'
				+ tmpOptions
			+ '</select>'
			+ '<button class="bp-act" onclick="' + tmpSelf + '.print()" title="Compile and print this blueprint as a sheet set">Print</button>'
			+ this._printAdvisoryHTML(tmpChosen)
		+ '</span>';
	}

	/** The chosen size, kept on the view so a repaint cannot quietly discard it. */
	bpPrintSheet(pSheet)
	{
		this._printSheet = String(pSheet || 'Letter');
		// Fetch (or reuse) the advisory for the newly chosen size, then repaint so it shows under the control.
		this._ensureResolutionChecked(this._printSheet);
		this.render();
	}

	/**
	 * The resolution preflight for one size, fetched once and cached on the view. It compiles on the server
	 * (a database walk, not a render), so a size change is cheap; the result drives the advisory below the
	 * print control. A failed check caches an empty result so the button is never held up by it.
	 */
	_ensureResolutionChecked(pSheet)
	{
		let tmpSheet = pSheet || 'Letter';
		this._resolutionBySheet = this._resolutionBySheet || {};
		this._resolutionChecking = this._resolutionChecking || {};
		if (this._resolutionBySheet[tmpSheet] || this._resolutionChecking[tmpSheet]) { return; }
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.checkURL !== 'function' || !this._subject || typeof fetch !== 'function') { return; }
		let tmpURL = tmpAPI.checkURL(this._subject, tmpSheet);
		if (!tmpURL) { return; }
		this._resolutionChecking[tmpSheet] = true;
		fetch(tmpURL, { credentials: 'same-origin' })
			.then((pResponse) => (pResponse.ok ? pResponse.json() : null))
			.then((pData) =>
			{
				this._resolutionChecking[tmpSheet] = false;
				this._resolutionBySheet[tmpSheet] = (pData && pData.Resolution) ? pData.Resolution : { Low: [], Checked: 0 };
				this.render();
			})
			.catch(() =>
			{
				this._resolutionChecking[tmpSheet] = false;
				this._resolutionBySheet[tmpSheet] = { Low: [], Checked: 0 };
			});
	}

	/** A muted advisory under the print control when a picture is too few pixels for the chosen size. */
	_printAdvisoryHTML(pSheet)
	{
		let tmpRes = (this._resolutionBySheet || {})[pSheet];
		if (!tmpRes || !tmpRes.Low || !tmpRes.Low.length) { return ''; }
		let tmpFloor = tmpRes.Floor || 150;
		let tmpList = tmpRes.Low.map((pImage) => pImage.Title + ' (' + pImage.WidthPx + 'px, ' + pImage.PPI + ' ppi)').join('\n');
		let tmpNoun = (tmpRes.Low.length === 1) ? 'image' : 'images';
		return '<span class="bp-print-advisory" title="' + this._attr('Below ' + tmpFloor + ' ppi at this size:\n' + tmpList) + '">'
			+ '<span class="bp-print-advisory-dot">&#9650;</span> '
			+ tmpRes.Low.length + ' ' + tmpNoun + ' may print soft at this size</span>';
	}

	_attr(pText)
	{
		return String(pText == null ? '' : pText)
			.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	_actionsHTML()
	{
		let tmpSelf = this._selfRef();
		// Compose and Details are here because this page took over /blueprint/:id from the generic record
		// pane. That pane could not show a blueprint's contents, which is the bug this fixes -- but it DID
		// carry the curation entry point, the name and description editor, and the comment thread. Routing
		// past it without carrying those forward would trade one missing thing for three.
		// "Details" was the only way to rename a blueprint and it did not say so. Somebody looking for the
		// name went round the page, tried the owner button, landed on the list, and concluded a composed
		// blueprint cannot be edited at all -- which is what it looks like from outside.
		// OFFERED WHENEVER THERE IS A BLUEPRINT ON SCREEN, not only when it already has a record. Gating on
		// this._id hid both controls on exactly the entity blueprints that have no record yet, which is
		// the case _ensureRecordID exists to handle.
		let tmpCurate = this._id
			? ('<button class="bp-act" onclick="' + tmpSelf + '.design()" title="Arrange this blueprint on a grid for web and print at any size, in a drafting or blueprint style">Design</button>'
				+ '<button class="bp-act" onclick="' + tmpSelf + '.compose()" title="Pin and unpin what this blueprint gathers, and set it as the plan sheet cover">Compose</button>'
				+ '<button class="bp-act bp-act-ghost" onclick="' + tmpSelf + '.details()" title="Rename it, edit its description, and read its documentation and comments">Edit details</button>'
				// TAKING CONTROL OF THE MAP IS AN ACTION SOMEBODY TAKES. A large sheet composes a map of the
				// set every time it prints, which is a fine default and nobody's decision. This turns it
				// into one: attach it, arrange it, and from then on the sheet prints the arranged version.
				+ '<button class="bp-act bp-act-ghost" onclick="' + tmpSelf + '.attachSetMap()" title="Save this set\'s map as a drawing you can rearrange. Once attached, large sheets print your version instead of composing one.">Attach set map</button>')
			: '';
		// A blueprint with nothing behind it has nothing to open. The button still earns its place as the
		// way back to the list, but it has to say that rather than name the page you are already on.
		let tmpBack = this._hasOwner()
			? ('<button class="bp-act bp-act-ghost" onclick="' + tmpSelf + '.back()">Open ' + this._ownerNoun() + '</button>')
			: ('<button class="bp-act bp-act-ghost" onclick="' + tmpSelf + '.back()" title="Back to every blueprint in this plan sheet">All blueprints</button>');
		return this._printSizesHTML()
			+ tmpCurate
			+ '<button class="bp-act" onclick="' + tmpSelf + '.copyLink()" title="Copy a link to this blueprint">Copy link</button>'
			+ tmpBack;
	}

	// Curation and the record pane both address the BLUEPRINT, so both need its record id. A blueprint
	// opened through its host does not have one until the payload arrives, which is why these read the
	// subject rather than this._id: after show() re-addresses, the subject is the record.
	_recordID()
	{
		if (this._subject && this._subject.Kind === 'record') { return this._subject.ID; }
		return Number((this._data && this._data.IDBlueprint) || 0);
	}

	/**
	 * The record id these two need, MINTING IT IF THIS ENTITY HAS NONE YET.
	 *
	 * An entity blueprint is computed, and GET /1.0/:EntityType/:id/Blueprint deliberately renders it
	 * with IDBlueprint 0 when no record exists rather than writing one -- a GET with a side effect is
	 * the worse trade, and that reasoning is right.
	 *
	 * But compose() and details() were guarded on that id, so on any entity whose record predates the
	 * create hook BOTH BUTTONS SILENTLY DID NOTHING. No navigation, no error, no clue: the two controls
	 * that curate and rename a blueprint simply had no effect, which reads as the page being broken.
	 *
	 * A click is not a GET. POST /1.0/:EntityType/:id/Blueprint exists for exactly this and is
	 * idempotent, so asking for it here is the explicit write the GET declines to make.
	 */
	_ensureRecordID()
	{
		let tmpID = this._recordID();
		if (tmpID) { return Promise.resolve(tmpID); }

		let tmpOwnerType = (this._subject && this._subject.Kind === 'entity')
			? this._subject.EntityKey : ((this._data && this._data.OwnerType) || '');
		let tmpIDOwner = (this._subject && this._subject.Kind === 'entity')
			? this._subject.ID : Number((this._data && this._data.IDOwner) || 0);
		if (!tmpOwnerType || !tmpIDOwner) { return Promise.resolve(0); }

		return libFrameworkClient.postJSON(this.pict, '/1.0/' + tmpOwnerType + '/' + tmpIDOwner + '/Blueprint', {})
			.then((pResult) =>
			{
				let tmpMinted = Number((pResult && pResult.IDBlueprint) || 0);
				// Keep it, so a second click does not ask again.
				if (tmpMinted && this._data) { this._data.IDBlueprint = tmpMinted; }
				return tmpMinted;
			})
			.catch(() => 0);
	}

	/**
	 * Attach this set's map as a Drawing, so a person owns the layout from here on.
	 *
	 * Deliberately a POST rather than something printing does for you: composing a map is a default,
	 * attaching one is a decision, and the sheet prefers the attached drawing forever after.
	 */
	attachSetMap()
	{
		let tmpID = this._recordID();
		if (!tmpID) { return Promise.resolve(0); }
		return libFrameworkClient.postJSON(this.pict, '/1.0/Blueprint/' + tmpID + '/SetMap', {})
			.then((pResult) =>
			{
				let tmpMedia = (pResult && pResult.Media) || {};
				this._announce((pResult && pResult.Replaced) ? 'Set map refreshed.' : 'Set map attached.',
					'It is a drawing now: open it from this blueprint to rearrange it, and large sheets will print your version.');
				return tmpMedia.IDMedia || 0;
			})
			.catch((pError) =>
			{
				// The likeliest refusal is a set too big to map, and its message says the number, so it is
				// worth showing rather than replacing with something generic.
				this._announce('Could not attach the set map.', (pError && pError.message) || '', 'error');
				return 0;
			});
	}

	/**
	 * Tell the person what happened, the way this page already does it.
	 *
	 * A TOAST, not an alert. The first version fell back to window.alert, which BLOCKS THE PAGE until
	 * somebody dismisses it -- the browser stops running anything, including whatever the click was
	 * still doing. It is a bad control on its own terms and it hung an end-to-end run for twelve
	 * minutes, which is how it was found. copyLink two methods up was already doing this correctly.
	 */
	_announce(pTitle, pDetail, pKind)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			return tmpModal.toast(pDetail ? (pTitle + ' ' + pDetail) : pTitle, { type: pKind || 'success' });
		}
		return null;
	}

	compose()
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		return this._ensureRecordID().then((pID) =>
		{
			if (tmpRouter && pID) { tmpRouter.navigate('/blueprint/' + pID + '/compose'); }
			return pID;
		});
	}

	// The design canvas: arrange this blueprint's layout (regions on a grid) for web + print at any size.
	design()
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		return this._ensureRecordID().then((pID) =>
		{
			if (tmpRouter && pID) { tmpRouter.navigate('/blueprint/' + pID + '/design'); }
			return pID;
		});
	}

	details()
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		return this._ensureRecordID().then((pID) =>
		{
			if (tmpRouter && pID) { tmpRouter.navigate('/blueprint/' + pID + '/details'); }
			return pID;
		});
	}

	// A designed layout, if this blueprint has one -- the same {Grid,Regions} the studio edits and the PDF
	// prints, so the read page matches both. Null when it has never been designed (then the plate sheet below).
	_layoutData()
	{
		let tmpRaw = this._data && this._data.Layout;
		if (!tmpRaw) { return null; }
		try { let tmpLayout = (typeof tmpRaw === 'string') ? JSON.parse(tmpRaw) : tmpRaw; return (tmpLayout && Array.isArray(tmpLayout.Regions) && tmpLayout.Regions.length) ? tmpLayout : null; }
		catch (pErr) { return null; }
	}

	_sheetHTML()
	{
		let tmpLayout = this._loading ? null : this._layoutData();
		if (tmpLayout)
		{
			this._renderedLayout = true;
			let tmpGrid = tmpLayout.Grid || { Cols: 12, Rows: 16 };
			// A responsive layout takes a viewport-sized box (so its aspect follows the pane and the @container
			// query can switch arrangement); a fixed one keeps its grid-proportioned box exactly as before.
			this._responsiveLayout = (tmpLayout.Responsive === true);
			let tmpSheetAttr = this._responsiveLayout
				? ' class="bp-layout-sheet bp-layout-responsive"'
				: ' class="bp-layout-sheet" style="aspect-ratio: ' + tmpGrid.Cols + ' / ' + tmpGrid.Rows + '"';
			return '<div class="bp-layout-actions">' + this._actionsHTML() + '</div>'
				+ '<div' + tmpSheetAttr + '>'
					+ libLayout.renderSheet(tmpLayout, { Kit: tmpLayout.Kit || 'clean' })
				+ '</div>';
		}
		this._renderedLayout = false;
		this._responsiveLayout = false;
		return libBlueprintRender.renderSheet(this._data,
			{
				loading: this._loading,
				fIcon: (pType) => this._icon(pType),
				mediaBase: '/1.0/Media/',
				links: 'app',
				actionsHTML: this._actionsHTML()
			});
	}
}

// The print control sits in the sheet's own action row, so it inherits .bp-act and only needs the
// select beside it to match.
const PRINT_CSS = /*css*/`
	.bp-print-wrap { display: inline-flex; align-items: center; flex-wrap: wrap; gap: 6px; }
	.bp-print-sheet { height: 28px; box-sizing: border-box; background: #0c1830; border: 1px solid rgba(208,226,247,0.4); border-radius: 6px; color: #eef4fc; padding: 0 6px; font: inherit; font-size: 12px; }
	/* A muted amber advisory on the navy strip: a picture is too few pixels for the chosen sheet. Informational,
	   not an error -- the print still works; the hover lists each image and its effective ppi. */
	.bp-print-advisory { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; line-height: 1.3; color: #f2c879; cursor: help; white-space: nowrap; }
	.bp-print-advisory-dot { font-size: 9px; line-height: 1; }
	/* A designed layout shown on the read page: the actions above, then the sheet at its grid proportions. */
	.bp-layout-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 16px; }
	.bp-layout-sheet { width: 100%; max-width: 760px; margin: 0 auto; box-shadow: 0 10px 44px rgba(16, 24, 40, 0.22); }
	/* A responsive layout widens to the pane and takes a resolved height (the inner .bpl is container-type:size
	   and will not derive height from content), so its aspect follows the pane and the sheet rearranges. */
	.bp-layout-responsive { max-width: 1180px; height: min(80vh, 860px); }
`;

module.exports = PictViewBlueprint;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Blueprint',
	DefaultRenderable: 'Blueprint-Page',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSSPriority: 540,
	CSS: libBlueprintRender.SHEET_CSS + libShareUI.SHARE_CSS + PRINT_CSS + libLayout.SHEET_CSS,
	Templates:
	[
		{ Hash: 'Blueprint-Page', Template: /*html*/`<div class="bp-root"><div id="Blueprint-Share"></div><div id="Blueprint-Versions"></div><div id="Blueprint-Sheet"></div></div>` }
	],
	Renderables:
	[
		{ RenderableHash: 'Blueprint-Page', TemplateHash: 'Blueprint-Page', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};
